import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  CloseButton,
  Flex,
  type ToastId,
  useToast,
} from '@chakra-ui/react';
import { IconArrowUndoUp } from 'central-icons/IconArrowUndoUp';
import React from 'react';

interface ShowUndoToastOptions {
  message: string;
  onUndo: () => void;
  duration?: number;
}

export const useUndoToast = () => {
  const toast = useToast();
  const toastIdRef = React.useRef<ToastId | undefined>();

  const showUndoToast = ({
    message,
    onUndo,
    duration = 5000,
  }: ShowUndoToastOptions) => {
    const handleUndoClick = () => {
      onUndo();

      if (toastIdRef.current) {
        toast.update(toastIdRef.current, {
          status: 'info',
          duration: 2000,
          title: 'Page restored.',
          isClosable: true,
        });
      }
    };

    if (toastIdRef.current && toast.isActive(toastIdRef.current)) {
      toast.close(toastIdRef.current);
    }

    toastIdRef.current = toast({
      position: 'bottom',
      duration,
      render: ({ onClose }) => (
        <UndoToast
          message={message}
          onUndoClick={handleUndoClick}
          onClose={onClose}
        />
      ),
    });
  };

  return showUndoToast;
};

interface UndoToastProps {
  message: string;
  onUndoClick: () => void;
  onClose: () => void;
}

export const UndoToast = ({
  message,
  onUndoClick,
  onClose,
}: UndoToastProps) => {
  return (
    <Alert
      status="info"
      variant="solid"
      alignItems="center"
      borderRadius="md"
      boxShadow="md"
    >
      <AlertIcon />
      <Flex flex="1" align="center" justify="space-between">
        <AlertTitle mr={4}>{message}</AlertTitle>
        <Button
          leftIcon={<IconArrowUndoUp />}
          colorScheme="white"
          onClick={onUndoClick}
          size="sm"
          height="22px"
          mr={6}
        >
          Undo
        </Button>
        <CloseButton
          size="sm"
          onClick={onClose}
          position="absolute"
          insetEnd={1}
          top={1}
        />
      </Flex>
    </Alert>
  );
};
